import React, { useState, useEffect } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import shippingAPI from '../../services/shippingAPI';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import { Truck, Plus, Search } from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminOrders.css';

// ── Sub-components ──────────────────────────────────────────────────────────
import ShippingStats       from './Shipping/ShippingStats';
import ShippingMethodCard  from './Shipping/ShippingMethodCard';
import ShippingMethodModal from './Shipping/ShippingMethodModal';
import ShippingLabelModal  from './Shipping/ShippingLabelModal';

const AdminShipping = () => {
  const { t } = useTranslation();
  const [shippingMethods, setShippingMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [notification, setNotification] = useState({ show: false, type: '', message: '', details: '' });
  const [labelFormData, setLabelFormData] = useState({
    shippingMethodId: '',
    referenceNumber: '',
    trackingNumber: '',
    carrierTrackingNumber: '',
    sender: {
      name: '',
      companyName: 'MeccaFlex',
      address: '',
      postalCode: '',
      city: '',
      country: 'IT',
      phone: '',
      email: ''
    },
    recipient: {
      name: '',
      companyName: '',
      address: '',
      postalCode: '',
      city: '',
      country: '',
      phone: '',
      email: ''
    },
    weight: '',
    shippingCost: '',
    zone: '',
    estimatedDelivery: '',
    specialInstructions: '',
    sendEmail: false,
    language: 'it'
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    carrier: '',
    carrierCode: '',
    carrierWebsite: '',
    trackingUrlTemplate: '',
    carrierPhone: '',
    carrierEmail: '',
    type: 'standard',
    isActive: true,
    trackingAvailable: false,
    insuranceIncluded: false,
    requiresSignature: false,
    maxWeight: 30,
    minWeight: 0,
    packagingCost: 0,
    handlingTime: 1,
    deliveryGuarantee: false,
    saturdayDelivery: false,
    sundayDelivery: false,
    customsClearance: false,
    specialInstructions: '',
    zones: [{
      name: '',
      countries: [],
      regions: [],
      baseCost: 0,
      costPerKg: 0,
      freeShippingThreshold: 0,
      estimatedDays: {
        min: 1,
        max: 3
      }
    }]
  });

  useEffect(() => {
    loadShippingMethods();
  }, []);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, type: '', message: '', details: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  const showNotification = (type, message, details = '') => {
    setNotification({ show: true, type, message, details });
  };

  const loadShippingMethods = async () => {
    try {
      setLoading(true);
      const data = await shippingAPI.getAll();
      setShippingMethods(data);
    } catch (error) {
      console.error('Error loading shipping methods:', error);
      showNotification('error', t('admin.shipping.messages.saveError'), error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMethod = () => {
    setEditingMethod(null);
    setFormData({
      name: '',
      description: '',
      carrier: '',
      carrierCode: '',
      carrierWebsite: '',
      trackingUrlTemplate: '',
      carrierPhone: '',
      carrierEmail: '',
      type: 'standard',
      isActive: true,
      trackingAvailable: false,
      insuranceIncluded: false,
      requiresSignature: false,
      maxWeight: 30,
      minWeight: 0,
      packagingCost: 0,
      handlingTime: 1,
      deliveryGuarantee: false,
      saturdayDelivery: false,
      sundayDelivery: false,
      customsClearance: false,
      specialInstructions: '',
      zones: [{
        name: '',
        countries: [],
        regions: [],
        baseCost: 0,
        costPerKg: 0,
        freeShippingThreshold: 0,
        estimatedDays: {
          min: 1,
          max: 3
        }
      }]
    });
    setShowModal(true);
  };

  const handleEditMethod = (method) => {
    setEditingMethod(method);
    setFormData({
      name: method.name,
      description: method.description || '',
      carrier: method.carrier,
      carrierCode: method.carrierCode || '',
      carrierWebsite: method.carrierWebsite || '',
      trackingUrlTemplate: method.trackingUrlTemplate || '',
      carrierPhone: method.carrierPhone || '',
      carrierEmail: method.carrierEmail || '',
      type: method.type,
      isActive: method.isActive,
      trackingAvailable: method.trackingAvailable,
      insuranceIncluded: method.insuranceIncluded,
      requiresSignature: method.requiresSignature,
      maxWeight: method.maxWeight,
      minWeight: method.minWeight || 0,
      packagingCost: method.packagingCost || 0,
      handlingTime: method.handlingTime || 1,
      deliveryGuarantee: method.deliveryGuarantee || false,
      saturdayDelivery: method.saturdayDelivery || false,
      sundayDelivery: method.sundayDelivery || false,
      customsClearance: method.customsClearance || false,
      specialInstructions: method.specialInstructions || '',
      zones: method.zones.map(z => ({
        name: z.name,
        countries: z.countries || [],
        regions: z.regions || [],
        baseCost: z.baseCost,
        costPerKg: z.costPerKg || 0,
        freeShippingThreshold: z.freeShippingThreshold || 0,
        estimatedDays: {
          min: z.estimatedDays?.min || 1,
          max: z.estimatedDays?.max || 3
        }
      }))
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMethod) {
        await shippingAPI.update(editingMethod._id, formData);
        showNotification('success', t('admin.shipping.messages.saveSuccess'));
      } else {
        await shippingAPI.create(formData);
        showNotification('success', t('admin.shipping.messages.saveSuccess'));
      }
      
      setShowModal(false);
      loadShippingMethods();
    } catch (error) {
      console.error('Error saving shipping method:', error);
      showNotification('error', t('admin.shipping.messages.saveError'), error.message);
    }
  };

  const handleDeleteMethod = async (methodId) => {
    if (!window.confirm(t('admin.shipping.messages.deleteConfirm'))) return;
    
    try {
      await shippingAPI.delete(methodId);
      showNotification('success', t('admin.shipping.messages.deleteSuccess'));
      loadShippingMethods();
    } catch (error) {
      console.error('Error deleting shipping method:', error);
      showNotification('error', t('admin.shipping.messages.deleteError'), error.message);
    }
  };

  const handleToggleActive = async (methodId) => {
    try {
      await shippingAPI.toggleActive(methodId);
      showNotification('success', t('admin.shipping.messages.toggleSuccess'));
      loadShippingMethods();
    } catch (error) {
      console.error('Error toggling shipping method:', error);
      showNotification('error', t('admin.shipping.messages.saveError'), error.message);
    }
  };

  const addZone = () => {
    setFormData({
      ...formData,
      zones: [...formData.zones, {
        name: '',
        countries: [],
        regions: [],
        baseCost: 0,
        costPerKg: 0,
        freeShippingThreshold: 0,
        estimatedDays: {
          min: 1,
          max: 3
        }
      }]
    });
  };

  const removeZone = (index) => {
    if (formData.zones.length === 1) {
      showNotification('warning', t('admin.shipping.messages.zoneRequired'));
      return;
    }
    const newZones = formData.zones.filter((_, i) => i !== index);
    setFormData({ ...formData, zones: newZones });
  };

  const updateZone = (index, field, value) => {
    const newZones = [...formData.zones];
    if (field === 'countries') {
      // Parse comma-separated string to array
      newZones[index][field] = value.split(',').map(c => c.trim().toUpperCase()).filter(c => c);
    } else if (field === 'regions') {
      newZones[index][field] = value.split(',').map(r => r.trim()).filter(r => r);
    } else if (field.includes('.')) {
      // Handle nested fields like estimatedDays.min
      const [parent, child] = field.split('.');
      newZones[index][parent][child] = value;
    } else {
      newZones[index][field] = value;
    }
    setFormData({ ...formData, zones: newZones });
  };

  // Label generation handlers
  const handleOpenLabelModal = (method) => {
    setLabelFormData({
      ...labelFormData,
      shippingMethodId: method._id,
      estimatedDelivery: method.zones[0]?.estimatedDays?.max || ''
    });
    setShowLabelModal(true);
  };

  const handleGenerateLabel = async () => {
    try {
      const response = await shippingAPI.generateLabel(labelFormData);
      
      // response is already a Blob (responseType: 'blob' set in shippingAPI)
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipping-label-${labelFormData.referenceNumber || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showNotification('success', t('admin.shipping.messages.labelGenerated'));
    } catch (error) {
      console.error('Error generating label:', error);
      showNotification('error', t('admin.shipping.messages.labelError'), error.message);
    }
  };

  const handleSendLabel = async () => {
    try {
      if (!labelFormData.recipient.email) {
        showNotification('error', t('admin.shipping.messages.emailRequired'));
        return;
      }

      await shippingAPI.sendLabel({
        ...labelFormData,
        recipientEmail: labelFormData.recipient.email
      });
      
      showNotification('success', t('admin.shipping.messages.labelSent'), labelFormData.recipient.email);
      setShowLabelModal(false);
    } catch (error) {
      console.error('Error sending label:', error);
      const errorMsg = error.response?.data?.hint || error.response?.data?.message || error.message;
      showNotification('error', t('admin.shipping.messages.sendError'), errorMsg);
    }
  };

  const handleLabelSubmit = async (e) => {
    e.preventDefault();
    
    if (labelFormData.sendEmail) {
      await handleSendLabel();
    } else {
      await handleGenerateLabel();
    }
  };

  const filteredMethods = shippingMethods.filter(method =>
    method.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    method.carrier.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const stats = {
    total: shippingMethods.length,
    active: shippingMethods.filter(m => m.isActive).length,
    avgDeliveryTime: shippingMethods.reduce((acc, m) => {
      const avgMethodTime = m.zones.reduce((sum, z) => 
        sum + ((z.estimatedDays.min + z.estimatedDays.max) / 2), 0
      ) / m.zones.length;
      return acc + avgMethodTime;
    }, 0) / (shippingMethods.length || 1),
    totalZones: shippingMethods.reduce((acc, m) => acc + m.zones.length, 0)
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      
      <div className="admin-content">
        {/* Elegant Notification */}
        {notification.show && (
          <div 
            className={`notification-toast notification-${notification.type}`}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 10000,
              minWidth: '350px',
              maxWidth: '500px',
              padding: '16px 20px',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              background: notification.type === 'success' ? '#10b981' :
                         notification.type === 'error' ? '#ef4444' :
                         notification.type === 'warning' ? '#f59e0b' : '#3b82f6',
              animation: 'slideInRight 0.3s ease-out',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div style={{ fontSize: '24px', flexShrink: 0 }}>
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontWeight: '600', 
                fontSize: '15px', 
                marginBottom: notification.details ? '4px' : '0',
                color: '#fff'
              }}>
                {notification.message}
              </div>
              {notification.details && (
                <div style={{ 
                  fontSize: '13px', 
                  opacity: 0.9,
                  color: '#fff',
                  marginTop: '4px'
                }}>
                  {notification.details}
                </div>
              )}
            </div>
            <button
              onClick={() => setNotification({ show: false, type: '', message: '', details: '' })}
              aria-label={t('admin.orders.closeNotification')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
                flexShrink: 0
              }}
            >
              ×
            </button>
          </div>
        )}

        <header className="content-header">
          <div>
            <h1>{t('admin.shipping.title')}</h1>
            <p>{t('admin.shipping.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <LanguageToggle />
            <button onClick={handleCreateMethod} className="admin-btn admin-btn-primary">
              <Plus size={18} />
              {t('admin.shipping.new')}
            </button>
          </div>
        </header>

        {/* Statistics */}
        <ShippingStats stats={stats} />

        {/* Search */}
        <div className="filters-bar">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder={t('admin.shipping.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Shipping Methods List */}
        <div className="quotes-list">
          {loading ? (
            <div className="loading-spinner">{t('admin.common.loading')}</div>
          ) : filteredMethods.length === 0 ? (
            <div className="empty-state">
              <Truck size={48} />
              <p>{t('admin.shipping.empty')}</p>
            </div>
          ) : (
            filteredMethods.map(method => (
              <ShippingMethodCard
                key={method._id}
                method={method}
                onEdit={handleEditMethod}
                onDelete={handleDeleteMethod}
                onToggle={handleToggleActive}
                onGenerateLabel={handleOpenLabelModal}
              />
            ))
          )}
        </div>

        {/* Add / Edit Method Modal */}
        {showModal && (
          <ShippingMethodModal
            editingMethod={editingMethod}
            formData={formData}
            setFormData={setFormData}
            onZoneAdd={addZone}
            onZoneRemove={removeZone}
            onZoneUpdate={updateZone}
            onSubmit={handleSubmit}
            onClose={() => setShowModal(false)}
          />
        )}

        {/* Shipping Label Modal */}
        {showLabelModal && (
          <ShippingLabelModal
            labelFormData={labelFormData}
            setLabelFormData={setLabelFormData}
            onSubmit={handleLabelSubmit}
            onClose={() => setShowLabelModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminShipping;
